import React from 'react';
import { Controller } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import type { AdditionalVehicleDetailsFormInputs } from '../../schemas/additionalvehicledetails.schema';
import { EstimateAcceptField } from './EstimateAcceptField';

interface MileageFieldsProps {
    control: Control<AdditionalVehicleDetailsFormInputs>;
    watchedValues: Pick<AdditionalVehicleDetailsFormInputs, 'mileage' | 'annualMileage'>;
    fieldIssues: Partial<Record<'mileage' | 'annualMileage', string>>;
    estimate: number | null | undefined;
    annualEstimate: number | null | undefined;
    isMotLoading: boolean;
}

export const MileageFields: React.FC<MileageFieldsProps> = ({
    control,
    watchedValues,
    fieldIssues,
    estimate,
    annualEstimate,
    isMotLoading,
}) => {
    return (
        <>
            {/*
                Issue #859 review follow-up — THE DISPLAYED VALUE COMES FROM `watch()`,
                NOT FROM `field.value`, for these two fields only. This is load-bearing
                and must not be "tidied" back to `field={field}`.

                An emptied odometer control stores `undefined` (EstimateAcceptField's
                `handleChange`), which is what distinguishes an explicit user clear from
                the `null` the form seeds when an unaccepted MOT estimate is on offer;
                `mergeOdometerReading` (QuoteContext.tsx) preserves the stored reading
                for the latter and clears for the former.

                But `useController` CANNOT READ BACK AN UNDEFINED VALUE. Verified
                empirically against the installed react-hook-form 7.60.0 with a
                Controller-registered field defaulted to 40000 and then set to
                `undefined`: `field.value` reads 40000, while `watch()` and both
                `getValues()` forms read `undefined`. `useController` resolves its value
                through `get(_formValues, name, get(_defaultValues, name))`, and RHF's
                `get` returns the fallback for an explicitly-undefined key — the SAME
                trait documented at length on the reset effect in
                `useAdditionalVehicleDetailsFormState.ts`, in the opposite
                direction. `watch()` with no arguments returns `_formValues` itself and
                applies no fallback, so it is the honest read.

                Sourced from `field.value`, clearing a prefilled odometer put the old
                number straight back in the box on the next render while the submitted
                payload correctly cleared it — Redux right, screen wrong. Pinned by
                "clearing a VALID stored mileage/annual mileage removes it" in
                VehicleSelectionStep.clear-repair.test.tsx and by B3 in
                AdditionalVehicleDetailsForm.prepopulate.test.tsx.

                This is the SAME mechanism the `vin`/`vrm` inputs (`VehicleIdentifierFields.tsx`) already use
                (`value={watchedValues.vin || ''}`), for the same documented reason —
                see layer 3 of the three-layer analysis in
                docs/features/quote-service-write-contract.md. Those two fields reached
                it first because clearing them was always spelled `undefined`; the
                odometer pair only needs it now that an explicit clear is spelled that
                way here too. One pattern, applied consistently.

                `onChange` still comes from the Controller, and `fieldState.error` is
                unaffected; only the value read is redirected.
            */}
            <div>
                <Controller
                    name="mileage"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <EstimateAcceptField
                            id="mileage"
                            label="Current Mileage"
                            estimate={estimate}
                            unitSuffix="mi"
                            emptyPlaceholder="Enter current mileage"
                            isLoading={isMotLoading}
                            field={{ value: watchedValues.mileage, onChange: field.onChange }}
                            error={error?.message ?? fieldIssues.mileage}
                            errorId="mileage-message"
                            hasFieldError={!!error}
                        />
                    )}
                />
            </div>
            <div>
                <Controller
                    name="annualMileage"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <EstimateAcceptField
                            id="annualMileage"
                            label="Annual Mileage"
                            estimate={annualEstimate}
                            unitSuffix="mi/yr"
                            emptyPlaceholder="Enter annual mileage"
                            isLoading={isMotLoading}
                            field={{ value: watchedValues.annualMileage, onChange: field.onChange }}
                            error={error?.message ?? fieldIssues.annualMileage}
                            errorId="annualMileage-message"
                            hasFieldError={!!error}
                        />
                    )}
                />
            </div>
        </>
    );
};
